import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Plus, Target as TargetIcon } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { Chip, ProgressBar, SectionTitle, EmptyState } from '@/components/Primitives';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { FormDrawer, TextField, SelectField } from '@/components/FormControls';
import api from '@/lib/api';
import { categoryChip, statusChip, priorityDot, TASK_PRIORITIES } from '@/lib/format';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, tEnum } = useLang();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', priority: 'Medium' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, tRes] = await Promise.all([api.get('/projects'), api.get('/tasks')]);
      const proj = pRes.data.find((p) => p.id === id);
      setProject(proj || null);
      setTasks(tRes.data.filter((task) => task.relatedProjectId === id));
    } catch (e) {
      toast.error(t('projectDetail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  const toggleTask = async (task) => {
    try {
      await api.put(`/tasks/${task.id}`, { status: task.status === 'Done' ? 'To Do' : 'Done' });
      load();
    } catch (e) {
      toast.error(t('projectDetail.updateError'));
    }
  };

  const addTask = async () => {
    if (!newTask.title.trim()) { toast.error(t('projectDetail.titleRequired')); return; }
    setSaving(true);
    try {
      await api.post('/tasks', {
        title: newTask.title, priority: newTask.priority, status: 'To Do', relatedProjectId: id,
      });
      toast.success(t('projectDetail.added'));
      setNewTask({ title: '', priority: 'Medium' });
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(t('projectDetail.addError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <FullScreenLoader label={t('projectDetail.loader')} />;
  if (!project) {
    return (
      <AppLayout>
        <AppHeader title={t('projectDetail.title')} back />
        <div className="mt-6"><EmptyState title={t('projectDetail.notFoundTitle')} subtitle={t('projectDetail.notFoundSubtitle')} /></div>
      </AppLayout>
    );
  }

  const doneCount = tasks.filter((task) => task.status === 'Done').length;

  return (
    <AppLayout>
      <AppHeader title={project.name} subtitle={tEnum(project.category)} back />

      <div className="mt-3 space-y-4">
        <div className="glass-card rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <Chip className={categoryChip(project.category)}>{tEnum(project.category)}</Chip>
            <Chip className={statusChip(project.status)}>{tEnum(project.status)}</Chip>
          </div>
          {project.description ? <p className="mt-3 text-sm leading-6 text-foreground/70">{project.description}</p> : null}
          <div className="mt-3 flex items-center justify-between text-xs text-foreground/55">
            <span>{t('common.progress')}</span>
            <span className="font-mono-plex text-brand-cyan">{project.progress}%</span>
          </div>
          <ProgressBar value={project.progress} className="mt-1.5" />
        </div>

        <div data-testid="project-detail-next-action" className="rounded-2xl border border-brand-cyan/20 bg-brand-cyan/[0.08] p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-cyan">{t('projectDetail.nextActionLabel')}</p>
          <p className="mt-1.5 text-sm text-foreground/90">{project.nextAction || t('projectDetail.nextActionEmpty')}</p>
        </div>

        <div>
          <SectionTitle
            action={
              <button
                data-testid="project-add-task-button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-cyan"
              >
                <Plus className="h-3.5 w-3.5" /> {t('projectDetail.addTask')}
              </button>
            }
          >
            {t('projectDetail.tasksHeading')} {tasks.length ? `(${doneCount}/${tasks.length})` : ''}
          </SectionTitle>
          <div className="space-y-2">
            {tasks.length === 0 ? (
              <EmptyState icon={TargetIcon} title={t('projectDetail.tasksEmptyTitle')} subtitle={t('projectDetail.tasksEmptySubtitle')} />
            ) : (
              tasks.map((task) => (
                <div key={task.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-3">
                  <button
                    data-testid={`project-task-toggle-${task.id}`}
                    onClick={() => toggleTask(task)}
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors',
                      task.status === 'Done' ? 'border-emerald-300 bg-emerald-400/20 text-emerald-300' : 'border-white/20 text-transparent hover:border-emerald-300'
                    )}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', priorityDot(task.priority))} />
                  <span className={cn('flex-1 text-sm', task.status === 'Done' ? 'text-foreground/40 line-through' : 'text-foreground/90')}>
                    {task.title}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={t('projectDetail.drawerTitle')}
        onSubmit={addTask}
        loading={saving}
        submitLabel={t('projectDetail.addTask')}
        submitTestId="project-task-save-button"
      >
        <TextField
          label={t('projectDetail.fieldTitle')}
          testId="project-task-title-input"
          value={newTask.title}
          onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
          placeholder={t('projectDetail.titlePlaceholder')}
        />
        <SelectField
          label={t('projectDetail.priority')}
          testId="project-task-priority-select"
          value={newTask.priority}
          onValueChange={(v) => setNewTask({ ...newTask, priority: v })}
          options={TASK_PRIORITIES}
        />
      </FormDrawer>
    </AppLayout>
  );
}
